import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap';
import PageHeader from '@spark/ui-components/components/BreadCrumb';
import { Card, CardContent, CircularProgress, Typography } from '@material-ui/core';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { getOuvidoriaDashboard } from '@spark/services';
import {
  buildHeatmapColumns,
  describeArc,
  getHeatColor,
  polarToCartesian,
} from './utils/dashboardOuvidoriaUtils';

const MESES = [
  { key: 'janeiro', label: 'Jan' },
  { key: 'fevereiro', label: 'Fev' },
  { key: 'marco', label: 'Mar' },
  { key: 'abril', label: 'Abr' },
  { key: 'maio', label: 'Mai' },
  { key: 'junho', label: 'Jun' },
  { key: 'julho', label: 'Jul' },
  { key: 'agosto', label: 'Ago' },
  { key: 'setembro', label: 'Set' },
  { key: 'outubro', label: 'Out' },
  { key: 'novembro', label: 'Nov' },
  { key: 'dezembro', label: 'Dez' },
];

const numberFormatter = new Intl.NumberFormat('pt-BR');
const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

const getGaugeStatus = value => {
  if (value >= 70) {
    return {
      label: 'Critico',
      color: '#d32f2f',
      description: 'Muitas pendências e poucas resoluções.',
    };
  }

  if (value >= 40) {
    return {
      label: 'Atencao',
      color: '#ed6c02',
      description: 'Acumulou relevante número de ouvidorias pendentes.',
    };
  }

  return {
    label: 'Saudavel',
    color: '#2e7d32',
    description: 'Fluxo sob controle e boa relação de resolução.',
  };
};

const OuvidoriaPressureGauge = ({ value, pendentes, finalizadas }) => {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    let frameId = 0;
    let startTimestamp = 0;
    const duration = 900;

    setAnimatedValue(0);

    const animate = timestamp => {
      if (!startTimestamp) {
        startTimestamp = timestamp;
      }

      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easedProgress = 1 - ((1 - progress) * (1 - progress));
      setAnimatedValue(safeValue * easedProgress);

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate);
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [safeValue]);

  const currentValue = Math.max(0, Math.min(100, animatedValue));
  const angle = (currentValue / 100) * 180;
  const needle = polarToCartesian(110, 110, 66, angle);
  const gaugeStatus = getGaugeStatus(safeValue);

  return (
    <div
      style={{
        border: '1px solid rgba(0,0,0,0.10)',
        borderRadius: 10,
        background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 4 }}>
          Medidor de pressão da ouvidoria
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Relação entre registros.
        </Typography>
      </div>

      <div
        style={{
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
          <svg viewBox="0 0 220 150" width="100%" role="img" aria-label="Medidor de pressao da ouvidoria">
            <path
              d={describeArc(110, 110, 72, 0, 180)}
              fill="none"
              stroke="#e6ebf2"
              strokeWidth="24"
              strokeLinecap="butt"
            />
            <path
              d={describeArc(110, 110, 72, 0, 60)}
              fill="none"
              stroke="#2e7d32"
              strokeWidth="24"
              strokeLinecap="butt"
            />
            <path
              d={describeArc(110, 110, 72, 60, 120)}
              fill="none"
              stroke="#ed6c02"
              strokeWidth="24"
              strokeLinecap="butt"
            />
            <path
              d={describeArc(110, 110, 72, 120, 180)}
              fill="none"
              stroke="#d32f2f"
              strokeWidth="24"
              strokeLinecap="butt"
            />

            <text x="24" y="128" fill="#667085" fontSize="11" fontWeight="600">
              0
            </text>
            <text x="178" y="128" fill="#667085" fontSize="11" fontWeight="600">
              100
            </text>

            <line
              x1="110"
              y1="110"
              x2={needle.x}
              y2={needle.y}
              stroke="#253858"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <circle cx="110" cy="110" r="9" fill="#253858" />

            <text
              x="110"
              y="76"
              textAnchor="middle"
              fill={gaugeStatus.color}
              fontSize="17"
              fontWeight="700"
            >
              {Math.round(currentValue)}%
            </text>
            <text
              x="110"
              y="94"
              textAnchor="middle"
              fill="#667085"
              fontSize="9"
              fontWeight="600"
            >
              Indice de pressão
            </text>
          </svg>
        </div>

        <div
          style={{
            marginTop: 8,
            padding: 14,
            borderRadius: 8,
            background: `${gaugeStatus.color}12`,
            border: `1px solid ${gaugeStatus.color}33`,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: gaugeStatus.color }}>
              {gaugeStatus.label}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#344054' }}>
              Pendentes: {numberFormatter.format(pendentes)} | Finalizadas: {numberFormatter.format(finalizadas)}
            </Typography>
          </div>

          <Typography variant="body2" style={{ color: '#475467' }}>
            {gaugeStatus.description}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const DashboardOuvidoria = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [hoveredHeatmapDay, setHoveredHeatmapDay] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicial: '',
    dataFinal: '',
    ano: '',
  });

  const carregarDashboard = async (payload = {}) => {
    try {
      setLoading(true);
      setError('');
      const response = await getOuvidoriaDashboard(payload);
      setDashboard(response || null);
    } catch (err) {
      setError(err?.message || 'Nao foi possivel carregar o dashboard da ouvidoria.');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDashboard();
  }, []);

  const handleFiltroChange = event => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAplicarFiltros = event => {
    event.preventDefault();

    const payload = {
      dataInicial: filtros.dataInicial || undefined,
      dataFinal: filtros.dataFinal || undefined,
      ano: filtros.ano ? Number(filtros.ano) : undefined,
    };

    carregarDashboard(payload);
  };

  const handleLimparFiltros = () => {
    setFiltros({
      dataInicial: '',
      dataFinal: '',
      ano: '',
    });
    carregarDashboard({});
  };

  const cards = useMemo(() => {
    const cardValues = dashboard?.cards || {};

    return [
      {
        id: 'naoLidas',
        title: 'Ouvidorias em aberto',
        value: Number(cardValues?.naoLidas || 0),
        accent: '#d32f2f',
      },
      {
        id: 'criadasHoje',
        title: 'Ouvidorias criadas hoje',
        value: Number(cardValues?.criadasHoje || 0),
        accent: '#1976d2',
      },
      {
        id: 'finalizadas',
        title: 'Ouvidorias finalizadas',
        value: Number(cardValues?.finalizadas || 0),
        accent: '#2e7d32',
      },
      {
        id: 'emAnalise',
        title: 'Ouvidorias em analise',
        value: Number(cardValues?.emAnalise || 0),
        accent: '#ed6c02',
      },
    ];
  }, [dashboard]);

  const chartData = useMemo(() => {
    const grafico = dashboard?.graficoMensal || {};
    return MESES.map(mes => ({
      name: mes.label,
      value: Number(grafico?.[mes.key] || 0),
    }));
  }, [dashboard]);

  const priorityTags = useMemo(() => {
    const items = Array.isArray(dashboard?.tagsPrioridade)
      ? dashboard.tagsPrioridade
      : [];

    return items.map(item => ({
      tagId: Number(item?.tagId || 0),
      nomeTag: item?.nomeTag || '-',
      quantidadeOuvidorias: Number(item?.quantidadeOuvidorias || 0),
    }));
  }, [dashboard]);

  const gaugeMetrics = useMemo(() => {
    const cardValues = dashboard?.cards || {};
    const abertas = Number(cardValues?.naoLidas || 0);
    const emAnalise = Number(cardValues?.emAnalise || 0);
    const finalizadas = Number(cardValues?.finalizadas || 0);
    const pendentes = abertas + emAnalise;
    const totalConsiderado = pendentes + finalizadas;
    const indicePressao = totalConsiderado > 0
      ? (pendentes / totalConsiderado) * 100
      : 0;

    return {
      abertas,
      emAnalise,
      finalizadas,
      pendentes,
      indicePressao,
    };
  }, [dashboard]);

  const dailyHeatmap = useMemo(() => {
    const dias = Array.isArray(dashboard?.graficoDiario?.dias)
      ? dashboard.graficoDiario.dias
      : [];
    const maxQuantidade = dias.reduce(
      (maior, item) => Math.max(maior, Number(item?.quantidade || 0)),
      0
    );

    return {
      columns: buildHeatmapColumns(dias),
      maxQuantidade,
    };
  }, [dashboard]);

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <PageHeader
            title=""
            routeSegments={[
              { name: 'Inicio' },
              { name: 'Dashboard' },
              { name: 'Dashboard Ouvidoria' },
            ]}
          />
        </Col>
      </Row>

      {error && (
        <Row>
          <Col md={12}>
            <Alert variant="danger" className="d-flex justify-content-between align-items-center">
              <span>{error}</span>
              <Button variant="outline-danger" size="sm" onClick={carregarDashboard}>
                Tentar novamente
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={12}>
          <Card elevation={0} style={{ border: '1px solid rgba(0,0,0,0.10)', width: '100%' }}>
            <CardContent style={{ padding: '16px 16px 26px' }}>
              <Form onSubmit={handleAplicarFiltros}>
                <Row className="align-items-end">
                  <Col md={3} sm={6} xs={12}>
                    <Form.Group>
                      <Form.Label>Data inicial</Form.Label>
                      <Form.Control
                        type="date"
                        name="dataInicial"
                        value={filtros.dataInicial}
                        onChange={handleFiltroChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3} sm={6} xs={12}>
                    <Form.Group>
                      <Form.Label>Data final</Form.Label>
                      <Form.Control
                        type="date"
                        name="dataFinal"
                        value={filtros.dataFinal}
                        onChange={handleFiltroChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2} sm={6} xs={12}>
                    <Form.Group>
                      <Form.Label>Ano</Form.Label>
                      <Form.Control
                        type="number"
                        name="ano"
                        min="1900"
                        max="9999"
                        value={filtros.ano}
                        onChange={handleFiltroChange}
                        placeholder="2026"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} sm={6} xs={12}>
                    <div className="d-flex gap-2">
                      <Button type="submit" disabled={loading}>
                        Aplicar
                      </Button>
                      <Button variant="outline-secondary" type="button" onClick={handleLimparFiltros} disabled={loading}>
                        Limpar
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardContent>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {cards.map(card => (
              <Card
                key={card.id}
                elevation={0}
                style={{
                  flex: '1 1 240px',
                  minWidth: 220,
                  border: '1px solid rgba(0,0,0,0.10)',
                }}
              >
                <div
                  style={{
                    height: 4,
                    width: '100%',
                    background: `linear-gradient(90deg, ${card.accent}, ${card.accent}33)`,
                  }}
                />
                <CardContent style={{ padding: 16 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {card.title}
                  </Typography>
                  {loading ? (
                    <div style={{ display: 'grid', placeItems: 'center', height: 48 }}>
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <Typography variant="h4" style={{ fontWeight: 700 }}>
                      {numberFormatter.format(card.value)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Card elevation={0} style={{ border: '1px solid rgba(0,0,0,0.10)', width: '100%' }}>
            <CardContent style={{ padding: 16 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                  flexWrap: 'wrap',
                  marginBottom: 14,
                }}
              >
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 4 }}>
                    Monitoramento de tags prioritárias
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Exibe todas as tags marcadas como prioridade e quantas ouvidorias não finalizadas elas possuem no período.
                  </Typography>
                </div>
              </div>

              {loading ? (
                <div style={{ display: 'grid', placeItems: 'center', height: 140 }}>
                  <CircularProgress />
                </div>
              ) : priorityTags.length === 0 ? (
                <div
                  style={{
                    padding: '18px 16px',
                    borderRadius: 10,
                    border: '1px dashed #d0d5dd',
                    background: '#f8fafc',
                    color: '#667085',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Nenhuma tag foi marcada como prioridade ate o momento.
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gap: 10,
                  }}
                >
                  {priorityTags.map((item, index) => (
                    <div
                      key={`${item.tagId}-${item.nomeTag}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: index === 0 ? 'linear-gradient(90deg, #fff7ed 0%, #ffffff 100%)' : '#ffffff',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                          style={{
                            minWidth: 28,
                            height: 28,
                            borderRadius: 8,
                            display: 'grid',
                            placeItems: 'center',
                            background: index === 0 ? '#f97316' : '#eef2f6',
                            color: index === 0 ? '#ffffff' : '#475467',
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#1f2937' }}>
                            {item.nomeTag}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#667085' }}>
                            Tag marcada como prioridade
                          </Typography>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 160,
                          padding: '8px 12px',
                          borderRadius: 999,
                          background: item.quantidadeOuvidorias > 0 ? '#fee2e2' : '#dcfce7',
                          color: item.quantidadeOuvidorias > 0 ? '#b42318' : '#166534',
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {numberFormatter.format(item.quantidadeOuvidorias)} nao finalizada(s)
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xl={8} lg={7} md={12} className="d-flex">
          <Card elevation={0} style={{ border: '1px solid rgba(0,0,0,0.10)', width: '100%' }}>
            <CardContent style={{ padding: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 12, fontWeight: 600 }}>
                Balanco mensal de ouvidorias
              </Typography>

              {loading ? (
                <div style={{ display: 'grid', placeItems: 'center', height: 320 }}>
                  <CircularProgress />
                </div>
              ) : (
                <div style={{ width: '100%', height: 360 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        formatter={value => numberFormatter.format(Number(value || 0))}
                        labelFormatter={label => `Mes: ${label}`}
                      />
                      <Bar dataKey="value" fill="#1f6fb2" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </Col>

        <Col xl={4} lg={5} md={12} className="mt-3 mt-lg-0 d-flex">
          {loading ? (
            <Card elevation={0} style={{ border: '1px solid rgba(0,0,0,0.10)', minHeight: 392, width: '100%' }}>
              <CardContent style={{ padding: 16 }}>
                <div style={{ display: 'grid', placeItems: 'center', height: 320 }}>
                  <CircularProgress />
                </div>
              </CardContent>
            </Card>
          ) : (
            <OuvidoriaPressureGauge
              value={gaugeMetrics.indicePressao}
              pendentes={gaugeMetrics.pendentes}
              finalizadas={gaugeMetrics.finalizadas}
            />
          )}
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Card elevation={0} style={{ border: '1px solid rgba(0,0,0,0.10)', marginBottom: 15 }}>
            <CardContent style={{ padding: 16 }}>
              <Typography variant="h6" style={{ marginBottom: 6, fontWeight: 600 }}>
                Mapa diário de criação de ouvidorias.
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Cada ponto representa um dia do período filtrado. Quanto mais escuro o verde, mais registros criados naquele dia.
              </Typography>

              {loading ? (
                <div style={{ display: 'grid', placeItems: 'center', height: 220 }}>
                  <CircularProgress />
                </div>
              ) : (
                <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                  <div
                    data-heatmap-wrapper="true"
                    style={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      minWidth: 'max-content',
                      width: '100%',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, minWidth: 'max-content' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateRows: '18px repeat(7, 16px)',
                          rowGap: 6,
                          width: 28,
                          flexShrink: 0,
                          color: '#667085',
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                      >
                        <div />
                        {WEEKDAY_LABELS.map(label => (
                          <div
                            key={label}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            {label}
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: 4 }}>
                        {dailyHeatmap.columns.map(column => (
                          <div
                            key={column.id}
                            style={{
                              display: 'grid',
                              gridTemplateRows: '18px repeat(7, 16px)',
                              rowGap: 6,
                              justifyItems: 'center',
                            }}
                          >
                            <div
                              style={{
                                minHeight: 18,
                                fontSize: 10,
                                color: '#667085',
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                width: 22,
                                textAlign: 'center',
                              }}
                            >
                              {column.monthLabel}
                            </div>

                            {Array.from({ length: 7 }, (_, weekday) => {
                              const item = column.items.find(entry => entry.weekday === weekday);

                              if (!item || item.empty) {
                                return (
                                  <div
                                    key={`${column.id}-${weekday}`}
                                    style={{ width: 16, height: 16 }}
                                  />
                                );
                              }

                              return (
                                <div
                                  key={item.data}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    background: getHeatColor(item.quantidade, dailyHeatmap.maxQuantidade),
                                    boxShadow: item.quantidade
                                      ? 'inset 0 0 0 1px rgba(15, 23, 42, 0.05)'
                                      : 'inset 0 0 0 1px rgba(148, 163, 184, 0.25)',
                                    cursor: 'pointer',
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                  }}
                                  onMouseEnter={event => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    const containerRect = event.currentTarget
                                      .closest('[data-heatmap-wrapper="true"]')
                                      ?.getBoundingClientRect();

                                    setHoveredHeatmapDay({
                                      data: item.dayLabel,
                                      quantidade: item.quantidade,
                                      left: rect.left - (containerRect?.left || 0) + rect.width / 2,
                                      top: rect.top - (containerRect?.top || 0),
                                      placeBelow: rect.top - (containerRect?.top || 0) < 36,
                                    });
                                    event.currentTarget.style.transform = 'scale(1.18)';
                                    event.currentTarget.style.boxShadow = '0 0 0 2px rgba(15, 23, 42, 0.12)';
                                  }}
                                  onMouseLeave={event => {
                                    setHoveredHeatmapDay(null);
                                    event.currentTarget.style.transform = 'scale(1)';
                                    event.currentTarget.style.boxShadow = item.quantidade
                                      ? 'inset 0 0 0 1px rgba(15, 23, 42, 0.05)'
                                      : 'inset 0 0 0 1px rgba(148, 163, 184, 0.25)';
                                  }}
                                />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateRows: '18px repeat(7, 16px)',
                        rowGap: 6,
                        justifyItems: 'center',
                        marginLeft: 14,
                        color: '#667085',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <div />
                      <span style={{ alignSelf: 'start' }}>Maior</span>
                      {[1, 0.75, 0.5, 0.25, 0].map(step => (
                        <div
                          key={step}
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: step === 0
                              ? '#e5e7eb'
                              : getHeatColor(
                                  Math.max(1, Math.round(dailyHeatmap.maxQuantidade * step)),
                                  Math.max(1, dailyHeatmap.maxQuantidade)
                                ),
                          }}
                        />
                      ))}
                      <span style={{ alignSelf: 'end' }}>Menor</span>
                    </div>

                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      {hoveredHeatmapDay && (
                        <div
                          style={{
                            position: 'absolute',
                            left: hoveredHeatmapDay.left,
                            top: hoveredHeatmapDay.placeBelow
                              ? hoveredHeatmapDay.top + 22
                              : hoveredHeatmapDay.top - 10,
                            transform: hoveredHeatmapDay.placeBelow
                              ? 'translate(-50%, 0)'
                              : 'translate(-50%, -100%)',
                            background: '#111827',
                            color: '#ffffff',
                            padding: '8px 10px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.20)',
                            whiteSpace: 'nowrap',
                            zIndex: 2,
                          }}
                        >
                          {hoveredHeatmapDay.data}: {numberFormatter.format(hoveredHeatmapDay.quantidade)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DashboardOuvidoria;
